import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const PlatformPreviewModal = ({ isOpen, onClose, platform, content, mediaItems = [], currentUser }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  // Reset index when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentMediaIndex(0);
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen || mediaItems.length <= 1) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentMediaIndex, mediaItems.length]);

  const handleNext = () => {
    setCurrentMediaIndex((prev) => (prev + 1) % mediaItems.length);
  };

  const handlePrevious = () => {
    setCurrentMediaIndex((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
  };

  if (!isOpen) return null;

  // Platform ID'sini al (obje veya string olabilir)
  const platformId = typeof platform === 'string' ? platform : platform?.id;
  const platformUsername = platform?.username;
  const platformProfilePicture = platform?.profilePicture || platform?.picture || currentUser?.photoURL;

  const platformInfo = {
    instagram: { name: 'Instagram', icon: '📷', logo: '/logos/instagram.png', bgColor: 'from-purple-600 to-pink-600' },
    facebook: { name: 'Facebook', icon: '👍', logo: '/logos/facebook.png', bgColor: 'from-blue-600 to-blue-700' },
    twitter: { name: 'Twitter (X)', icon: '🐦', logo: '/logos/twitter.png', bgColor: 'from-blue-400 to-blue-500' },
    linkedin: { name: 'LinkedIn', icon: '💼', logo: '/logos/linkedin.png', bgColor: 'from-blue-700 to-blue-800' },
    tiktok: { name: 'TikTok', icon: '🎵', logo: '/logos/tiktok.png', bgColor: 'from-black to-gray-800' },
    youtube: { name: 'YouTube', icon: '▶️', logo: '/logos/youtube.png', bgColor: 'from-red-600 to-red-700' },
    pinterest: { name: 'Pinterest', icon: '📌', logo: '/logos/pinterest.png', bgColor: 'from-red-500 to-red-600' },
    snapchat: { name: 'Snapchat', icon: '👻', logo: '/logos/snapchat.png', bgColor: 'from-yellow-400 to-yellow-500' },
    reddit: { name: 'Reddit', icon: '🤖', logo: '/logos/reddit.png', bgColor: 'from-orange-600 to-red-600' },
    tumblr: { name: 'Tumblr', icon: '📝', logo: '/logos/tumblr.png', bgColor: 'from-blue-800 to-indigo-900' },
    whatsapp: { name: 'WhatsApp Business', icon: '💬', logo: '/logos/whatsapp.png', bgColor: 'from-green-500 to-green-600' },
    telegram: { name: 'Telegram', icon: '✈️', logo: '/logos/telegram.png', bgColor: 'from-blue-400 to-blue-500' },
    discord: { name: 'Discord', icon: '🎮', logo: '/logos/discord.png', bgColor: 'from-indigo-600 to-purple-600' },
    threads: { name: 'Threads', icon: '🧵', logo: '/logos/threads.png', bgColor: 'from-black to-gray-800' },
    mastodon: { name: 'Mastodon', icon: '🐘', logo: '/logos/mastodon.png', bgColor: 'from-purple-600 to-indigo-600' },
    bluesky: { name: 'Bluesky', icon: '🦋', logo: '/logos/bluesky.png', bgColor: 'from-blue-500 to-blue-600' }
  };

  const info = platformInfo[platformId] || { name: platformId, icon: '🌐', logo: null, bgColor: 'from-gray-600 to-gray-700' };
  const currentMedia = mediaItems[currentMediaIndex];

  // Profil bilgilerini hazırla
  const displayName = platformUsername
    ? (platformUsername.startsWith('@') ? platformUsername : `@${platformUsername}`)
    : (platform?.name || currentUser?.displayName || 'Sizin Hesabınız');
  const hasProfilePicture = !!platformProfilePicture;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className={`bg-gradient-to-r ${info.bgColor} p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {info.logo ? (
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center p-2">
                  <img src={info.logo} alt={info.name} className="w-full h-full object-contain" />
                </div>
              ) : (
                <span className="text-4xl">{info.icon}</span>
              )}
              <div>
                <h2 className="text-2xl font-bold">{info.name} Önizleme</h2>
                <p className="text-sm opacity-90">Gönderiniz bu şekilde görünecek</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="border-2 border-gray-200 rounded-xl overflow-hidden">

            {/* Mock Post Header */}
            <div className="p-4 border-b border-gray-200 flex items-center gap-3">
              {hasProfilePicture ? (
                <img
                  src={platformProfilePicture}
                  alt={displayName}
                  className="w-12 h-12 rounded-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xl ${hasProfilePicture ? 'hidden' : ''}`}>
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-gray-800">{displayName}</p>
                <p className="text-xs text-gray-500">Şimdi</p>
              </div>
            </div>

            {/* Media (Image or Video) with Navigation */}
            {mediaItems.length > 0 && currentMedia && (
              <div className="bg-gray-100 relative">
                {currentMedia.type === 'video' ? (
                  <video
                    key={currentMediaIndex}
                    src={currentMedia.url}
                    className="w-full max-h-96 object-contain bg-black"
                    controls
                    preload="metadata"
                  />
                ) : (
                  <img src={currentMedia.url} alt="Preview" className="w-full max-h-96 object-contain" />
                )}

                {/* Navigation Arrows - Only show if multiple media */}
                {mediaItems.length > 1 && (
                  <>
                    {/* Left Arrow */}
                    <button
                      onClick={handlePrevious}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition"
                      aria-label="Önceki medya"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>

                    {/* Right Arrow */}
                    <button
                      onClick={handleNext}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition"
                      aria-label="Sonraki medya"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>

                    {/* Media Counter */}
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-3 py-1 rounded-full">
                      {currentMediaIndex + 1} / {mediaItems.length}
                    </div>

                    {/* Dot Indicators */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {mediaItems.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentMediaIndex(index)}
                          className={`w-2 h-2 rounded-full transition ${
                            index === currentMediaIndex
                              ? 'bg-white'
                              : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                          }`}
                          aria-label={`${index + 1}. medyaya git`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Content */}
            <div className="p-4">
              {content ? (
                <p className="text-gray-800 whitespace-pre-wrap">{content}</p>
              ) : (
                <p className="text-gray-400 italic">Metin girilmedi</p>
              )}
            </div>

            {/* Mock Interactions */}
            <div className="px-4 py-3 border-t border-gray-200 flex items-center gap-6 text-gray-600">
              <span className="text-sm">❤️ Beğen</span>
              <span className="text-sm">💬 Yorum Yap</span>
              <span className="text-sm">📤 Paylaş</span>
            </div>
          </div>

          {/* Info Box */}
          <div className="mt-4 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>💡 Not:</strong> Bu sadece bir önizlemedir. Gerçek gönderi platformun stil ve düzenine göre değişiklik gösterebilir.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-semibold"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlatformPreviewModal;
